import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Check, Crown, Zap, Trophy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNeedAuth: () => void;
}

const TIERS = {
  light: {
    name: "Light",
    price: "$1.69",
    trialText: "3 months free",
    icon: Zap,
    features: [
      "Full Elite access for first 5 days",
      "Basic team statistics",
      "League standings",
      "Game schedules",
      "Historical data",
      "Track up to 8 favorite players",
    ],
  },
  pro: {
    name: "Pro",
    price: "$2.79",
    trialText: "1 month free",
    icon: Crown,
    features: [
      "Full Elite access for first 5 days",
      "Everything in Light",
      "Player profiles",
      "Playoff predictions",
      "Track up to 8 favorite players",
    ],
  },
  elite: {
    name: "Elite",
    price: "$3.49",
    trialText: "1 month free",
    icon: Trophy,
    features: [
      "Everything in Pro",
      "Advanced analytics",
      "Export reports",
      "Full betting odds access",
      "Priority support",
      "Early access to features",
      "Track up to 24 favorite players",
    ],
    popular: true,
  },
};

const SubscriptionModal = ({ isOpen, onClose, onNeedAuth }: SubscriptionModalProps) => {
  const { session, subscription } = useAuth();
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const handleSubscribe = async (tier: "light" | "pro" | "elite") => {
    if (!session) {
      onNeedAuth();
      return;
    }

    setIsLoading(tier);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { tier },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      console.error("Checkout error:", error);
      toast.error(error.message || "Failed to start checkout");
    } finally {
      setIsLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!session) return;

    setIsLoading("manage");
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      console.error("Portal error:", error);
      toast.error(error.message || "Failed to open subscription portal");
    } finally {
      setIsLoading(null);
    }
  };

  const SubscriptionContent = () => (
    <>
      {subscription.subscribed ? (
        <div className="py-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full mb-4">
            <Crown className="w-5 h-5" />
            <span className="font-semibold capitalize">
              {subscription.tier} Plan {subscription.isTrial && "(Trial)"}
            </span>
          </div>
          {subscription.subscriptionEnd && (
            <p className="text-muted-foreground mb-6">
              {subscription.isTrial ? "Trial ends" : "Renews"} on{" "}
              {new Date(subscription.subscriptionEnd).toLocaleDateString()}
            </p>
          )}
          <Button
            onClick={handleManageSubscription}
            disabled={isLoading === "manage"}
            variant="outline"
          >
            {isLoading === "manage" ? "Loading..." : "Manage Subscription"}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4">
          {(Object.entries(TIERS) as [keyof typeof TIERS, typeof TIERS[keyof typeof TIERS]][]).map(
            ([key, tier]) => {
              const Icon = tier.icon;
              const isPopular = "popular" in tier && tier.popular;

              return (
                <div
                  key={key}
                  className={cn(
                    "relative rounded-xl border p-4 sm:p-6 transition-all hover:border-primary/50",
                    isPopular ? "border-primary bg-primary/5" : "border-border"
                  )}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                      Most Popular
                    </div>
                  )}

                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center",
                        isPopular ? "bg-primary text-primary-foreground" : "bg-muted"
                      )}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-lg">{tier.name}</h3>
                      <p className="text-xs text-primary font-medium">{tier.trialText}</p>
                    </div>
                  </div>

                  <div className="mb-4 sm:mb-6">
                    <span className="text-2xl sm:text-3xl font-bold">{tier.price}</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>

                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleSubscribe(key)}
                    disabled={isLoading === key}
                    className="w-full"
                    variant={isPopular ? "default" : "outline"}
                  >
                    {isLoading === key ? "Loading..." : `Start Free Trial`}
                  </Button>
                </div>
              );
            }
          )}
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground px-2">
        Light &amp; Pro get full Elite access for the first 5 days, then tier-specific features for the remaining trial period.
      </p>
    </>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader className="text-center">
            <DrawerTitle className="text-xl font-display">
              {subscription.subscribed ? "Your Subscription" : "Choose Your Plan"}
            </DrawerTitle>
            {!subscription.subscribed && (
              <p className="text-sm text-muted-foreground mt-1">
                Unlock premium hockey statistics and insights
              </p>
            )}
          </DrawerHeader>
          <div className="overflow-y-auto px-4 pb-6">
            <SubscriptionContent />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-4xl bg-card border-border max-h-[85vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl font-display text-center">
            {subscription.subscribed ? "Your Subscription" : "Choose Your Plan"}
          </DialogTitle>
          {!subscription.subscribed && (
            <p className="text-center text-muted-foreground mt-2 text-sm sm:text-base">
              Unlock premium hockey statistics and insights
            </p>
          )}
        </DialogHeader>
        <SubscriptionContent />
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionModal;
